import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, CircleNotch, Warning, LockKey, ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";
import { track } from "@/lib/track";

export const SandboxDemo = ({ onUnlock }) => {
  const { t } = useI18n();
  const [state, setState] = useState("idle"); // idle | scanning | done
  const REVEALED = t("sbx.revealed");
  const LOCKED = t("sbx.locked");
  const LOCKED_REFS = ["UCP 600 Art. 14(a)", "UCP 600 Art. 14(a)", "UCP 600 Art. 17", "UCP 600 Art. 18(c)"];
  const STEPS = t("sbx.steps");

  const run = () => {
    track("try_sample_clicked", { source: "landing_sandbox" });
    setState("scanning");
    setTimeout(() => setState("done"), 2000);
  };

  return (
    <section id="demo" className="relative border-y border-white/10 bg-lc-slate/20" data-testid="sandbox-demo">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-lc-sky">{t("sbx.overline")}</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">{t("sbx.title")}</h2>
          <p className="mt-3 text-sm text-lc-fog max-w-xl mx-auto">{t("sbx.sub")}</p>
        </div>

        {state === "idle" && (
          <div className="mt-8 flex justify-center">
            <button data-testid="sandbox-run-btn" onClick={run}
              className="group inline-flex items-center gap-2.5 rounded-sm bg-lc-sky px-6 py-3.5 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
              <MagnifyingGlass weight="bold" size={17} />
              {t("sbx.btn")}
              <ArrowRight weight="bold" size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {state === "scanning" && (
            <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              data-testid="sandbox-scanning" className="mt-10 rounded-sm border border-white/10 bg-lc-navy/60 p-10 flex flex-col items-center">
              <CircleNotch size={40} className="animate-spin text-lc-sky" />
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-lc-fog">{t("sbx.scanning")}</p>
              <div className="mt-6 w-full max-w-sm space-y-2">
                {STEPS.map((l, i) => (
                  <motion.div key={l} initial={{ opacity: 0.2 }} animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.25 }}
                    className="flex items-center gap-2 rounded-sm bg-lc-slate/40 px-3 py-2 text-xs text-lc-fog">
                    <span className="h-1.5 w-1.5 rounded-full bg-lc-sky" /> {l}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {state === "done" && (
            <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              data-testid="sandbox-results" className="mt-10">
              <div className="flex items-center justify-between gap-4 rounded-t-sm border border-lc-critical/30 bg-lc-critical/10 px-5 py-3">
                <span className="inline-flex items-center gap-2 text-sm font-bold text-lc-critical">
                  <Warning weight="fill" size={16} /> {t("sbx.header")}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-lc-critical/80">{t("sbx.readiness")}</span>
              </div>

              <div className="rounded-b-sm border border-t-0 border-white/10 bg-lc-navy/60 p-4 space-y-3">
                {REVEALED.map((d, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
                    data-testid={`sandbox-finding-${i}`} className="rounded-sm border border-lc-critical/25 bg-lc-slate/30 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                        <span className="rounded-sm bg-lc-critical/20 px-1.5 py-0.5 font-mono text-[10px] uppercase text-lc-critical">{t("sbx.critical")}</span>
                        {d.title}
                      </h4>
                      <span className="font-mono text-[10px] break-words text-lc-sky">{d.ref}</span>
                    </div>
                    <p className="mt-2 text-[11px] text-lc-fog">{d.doc}</p>
                    <div className="mt-2 grid gap-1.5 text-[11px]">
                      <div className="rounded-sm bg-lc-navy/60 px-2 py-1.5"><span className="font-semibold text-white">{t("sbx.lcReq")} </span><span className="text-lc-fog">{d.lc}</span></div>
                      <div className="rounded-sm bg-lc-navy/60 px-2 py-1.5"><span className="font-semibold text-white">{t("sbx.docShows")} </span><span className="text-lc-fog">{d.val}</span></div>
                    </div>
                  </motion.div>
                ))}

                {/* locked findings */}
                <div className="relative" data-testid="sandbox-locked">
                  <div className="space-y-3 select-none blur-[6px] pointer-events-none" aria-hidden="true">
                    {LOCKED.map((t2, i) => {
                      const isWarn = i === LOCKED.length - 1;
                      return (
                      <div key={i} className="rounded-sm border border-lc-critical/25 bg-lc-slate/30 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h4 className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                            {isWarn
                              ? <span className="rounded-sm bg-lc-warn/20 px-1.5 py-0.5 font-mono text-[10px] uppercase text-lc-warn">{t("ws.sev.warning")}</span>
                              : <span className="rounded-sm bg-lc-critical/20 px-1.5 py-0.5 font-mono text-[10px] uppercase text-lc-critical">{t("sbx.critical")}</span>}
                            {t2}
                          </h4>
                          <span className="font-mono text-[10px] break-words text-lc-sky">{LOCKED_REFS[i] || "UCP 600 Art. 28"}</span>
                        </div>
                        <div className="mt-2 h-8 rounded-sm bg-lc-navy/60" />
                      </div>
                      );
                    })}
                  </div>

                  <button data-testid="sandbox-unlock-btn" onClick={onUnlock}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-sm bg-lc-navy/50 backdrop-blur-[2px]">
                    <span className="inline-flex items-center gap-2 rounded-full border border-lc-sky/40 bg-lc-navy/80 px-4 py-2 text-sm font-semibold text-lc-sky">
                      <LockKey weight="fill" size={16} /> {t("sbx.unlock")}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-sm bg-lc-sky px-5 py-2.5 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
                      {t("sbx.startTrial")}
                      <ArrowRight weight="bold" size={15} />
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SandboxDemo;
